import {
  REGISTRY_BLOCK,
  pickSiteSocialLinksFromContent,
  type LayoutBlock,
  type SiteSocialNetworkKey,
} from '@web-studio/shared-schemas';
import {
  LUCIDE_SSR_BY_ICON_KEY,
  LUCIDE_SSR_PHONE,
  LUCIDE_SSR_X,
  resolveSectionSsrIcon,
  type LucideSsrIcon,
} from '../icons/lucideSsrIconNodes';
import { renderLucideIconHtml, renderSocialIconHtml } from '../icons/renderIconHtml';
import { SITE_SOCIAL_ICONS } from '../icons/socialNetworks';
import { pickBlockField } from '../lib/localized';
import { pickChrome } from '../lib/siteChromeCopy';
import { safeTelHref } from '../lib/safeGuestLinks';
import { escapeHtml } from './escape';

const NETWORK_LABEL: Record<SiteSocialNetworkKey, string> = {
  facebook: SITE_SOCIAL_ICONS.facebook.title,
  instagram: SITE_SOCIAL_ICONS.instagram.title,
  tiktok: SITE_SOCIAL_ICONS.tiktok.title,
  x: SITE_SOCIAL_ICONS.x.title,
  youtube: SITE_SOCIAL_ICONS.youtube.title,
};

const ARROW_UP_SSR: LucideSsrIcon = LUCIDE_SSR_BY_ICON_KEY.ArrowUp!;
const MESSAGES_SSR: LucideSsrIcon = LUCIDE_SSR_BY_ICON_KEY.MessagesSquare!;

export function renderBackToTopFloatHtml(block: LayoutBlock, locale: string): string {
  if (block.hidden === true) {
    return '';
  }
  const isPill = block.registryBlockKey === REGISTRY_BLOCK.presentation.backToTopPill;
  const arrangement = isPill ? 'pill' : 'corner';
  const ariaLabel =
    pickBlockField(block.content, 'ariaLabel', locale) ||
    pickChrome('backToTopAria', locale);
  const sectionIcon = resolveSectionSsrIcon(
    pickBlockField(block.content, 'sectionIcon', locale),
    ARROW_UP_SSR,
  );
  const iconHtml = renderLucideIconHtml(sectionIcon.iconNode, {
    size: isPill ? 32 : 'md',
    className: 'ws-back-top__icon',
    name: sectionIcon.name,
  });

  return `<div class="ws-float-chrome ws-back-top ws-back-top--${arrangement}" data-block-id="${escapeHtml(block.blockId)}" data-component="BackToTop_Float" data-back-top-arrangement="${arrangement}" data-ws-back-top hidden>
  <button type="button" class="ws-back-top__btn" aria-label="${escapeHtml(ariaLabel)}" data-ws-back-top-btn>
    <span class="ws-back-top__icon-hit">${iconHtml}</span>
  </button>
</div>`;
}

export function renderContactChannelsFloatHtml(block: LayoutBlock, locale: string): string {
  if (block.hidden === true) {
    return '';
  }
  const arrangement =
    block.registryBlockKey === REGISTRY_BLOCK.presentation.contactChannelsFab ? 'fab' : 'rail';
  const phone = pickBlockField(block.content, 'phone', locale).trim();
  const hasPhone = Boolean(phone) && phone !== '—';
  const telHref = safeTelHref(phone);
  const links = pickSiteSocialLinksFromContent(block.content, locale);
  if (!hasPhone && links.length === 0) {
    return '';
  }

  const regionLabel = pickChrome('contactChannelsRegion', locale);
  const expandLabel =
    pickBlockField(block.content, 'expandLabel', locale) ||
    pickChrome('contactChannelsExpand', locale);
  const collapseLabel = pickChrome('contactChannelsCollapse', locale);
  const phoneLabel = pickChrome('contactChannelsPhone', locale);
  const panelId = `ws-channels-${escapeHtml(block.blockId.slice(0, 8))}`;
  const phoneIcon = resolveSectionSsrIcon(
    pickBlockField(block.content, 'phoneIcon', locale),
    LUCIDE_SSR_PHONE,
  );
  const fabIcon = resolveSectionSsrIcon(
    pickBlockField(block.content, 'fabIcon', locale),
    MESSAGES_SSR,
  );

  const phoneHtml =
    hasPhone && telHref
      ? `<a class="ws-channels__link ws-channels__link--phone" href="${escapeHtml(telHref)}" aria-label="${escapeHtml(`${phoneLabel}: ${phone}`)}">${renderLucideIconHtml(phoneIcon.iconNode, { size: 'md', className: 'ws-channels__icon', name: phoneIcon.name })}<span class="ws-channels__sr">${escapeHtml(phone)}</span></a>`
      : '';

  const socialHtml = `<ul class="ws-channels__list">${links
    .map(({ network, url }) => {
      const icon = renderSocialIconHtml(network, { size: 'md' });
      return `<li><a class="ws-channels__link" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer" aria-label="${escapeHtml(NETWORK_LABEL[network])}">${icon}</a></li>`;
    })
    .join('')}</ul>`;

  const panel = `<div class="ws-channels__panel" id="${panelId}">${phoneHtml}${socialHtml}</div>`;
  const fab =
    arrangement === 'fab'
      ? `<button type="button" class="ws-channels__fab" aria-expanded="false" aria-controls="${panelId}" aria-label="${escapeHtml(expandLabel)}" data-ws-channels-toggle data-ws-channels-expand="${escapeHtml(expandLabel)}" data-ws-channels-collapse="${escapeHtml(collapseLabel)}"><span class="ws-channels__fab-glyph" aria-hidden="true">${renderLucideIconHtml(fabIcon.iconNode, { size: 'md', className: 'ws-channels__fab-icon ws-channels__fab-icon--share', name: fabIcon.name })}${renderLucideIconHtml(LUCIDE_SSR_X.iconNode, { size: 'md', className: 'ws-channels__fab-icon ws-channels__fab-icon--close', name: LUCIDE_SSR_X.name })}</span></button>`
      : '';

  return `<div class="ws-float-chrome ws-channels ws-channels--${arrangement}" data-block-id="${escapeHtml(block.blockId)}" data-component="ContactChannels_Float" data-channels-arrangement="${arrangement}" data-ws-channels role="region" aria-label="${escapeHtml(regionLabel)}">
  ${fab}${panel}
</div>`;
}
